import { useEffect } from "react";
import { usePhysicsStore } from "../../stores/physicsStore";
import { logger } from "../../utils/logger";
import { createExternalForceVizController } from "./useSceneDisposeOnUnmount/externalForceViz";
import { createScenePoseApplier } from "./useSceneDisposeOnUnmount/scenePoseApplication";

export function useSceneDisposeOnUnmount({
  externalForceVizRef,
  sceneRef,
  modelGroupRef,
  sceneObjectsRef,
  zeroBodySyncWarningSignatureRef,
  rolloutProbeRef,
  physicsPoseLogDoneRef,
  padPoseLogDoneRef,
  physicsPoseApplyLoggedRef,
}) {
  useEffect(() => {
    const {
      clearExternalForceViz,
      disposeAndRemove,
      mujocoVecToViewer,
      onActuatorControlPulse,
      onExternalForcePulse,
    } = createExternalForceVizController({ externalForceVizRef, sceneRef });

    const applyLatestPoses = createScenePoseApplier({
      externalForceVizRef,
      sceneRef,
      modelGroupRef,
      sceneObjectsRef,
      zeroBodySyncWarningSignatureRef,
      rolloutProbeRef,
      physicsPoseLogDoneRef,
      padPoseLogDoneRef,
      disposeAndRemove,
      mujocoVecToViewer,
    });

    // `usePhysicsStore` does not use subscribeWithSelector; use the plain
    // Zustand listener signature and filter latest pose changes manually.
    const unsub = usePhysicsStore.subscribe((state, prevState) => {
      if (state.latestPoses !== prevState.latestPoses) {
        applyLatestPoses(state.latestPoses);
      }
    });

    window.addEventListener("physics-external-force-pulse", onExternalForcePulse);
    window.addEventListener("physics-actuator-control-pulse", onActuatorControlPulse);
    window.addEventListener("physics-stopped-reset", clearExternalForceViz);

    // Apply current snapshot once
    const currentPoses = usePhysicsStore.getState().latestPoses;

    applyLatestPoses(currentPoses);
    const physicsPoseApplyLogged = physicsPoseApplyLoggedRef.current;

    return () => {
      physicsPoseLogDoneRef.current = false;
      padPoseLogDoneRef.current = false;
      if (physicsPoseApplyLogged && physicsPoseApplyLogged.clear) {
        physicsPoseApplyLogged.clear();
      }
      try {
        unsub();
      } catch (error) {
        logger.warn("[Scene] Unexpected error during scene update", error);
      }
      try {
        window.removeEventListener("physics-external-force-pulse", onExternalForcePulse);
      } catch {
        // Best-effort cleanup during teardown.
      }
      try {
        window.removeEventListener("physics-actuator-control-pulse", onActuatorControlPulse);
      } catch {
        // Best-effort cleanup during teardown.
      }
      try {
        window.removeEventListener("physics-stopped-reset", clearExternalForceViz);
      } catch {
        // Best-effort cleanup during teardown.
      }
      clearExternalForceViz();
    };
  }, [
    externalForceVizRef,
    modelGroupRef,
    padPoseLogDoneRef,
    physicsPoseApplyLoggedRef,
    physicsPoseLogDoneRef,
    rolloutProbeRef,
    sceneObjectsRef,
    sceneRef,
    zeroBodySyncWarningSignatureRef,
  ]);
}
